import React, { Suspense, lazy, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Container,
  Table,
  TableBody,
  TableCell as MuiTableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import Typography from '../components/Typography';
import InitialData from '../../InitialData';
import usePricePeriods from '../../hooks/usePricePeriods';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.secondary.light,
  },
}));

const TableCell = styled(MuiTableCell)(({ theme }) => ({
  fontSize: '1.1rem',
  lineHeight: '1.2rem',
  padding: 10,
  [theme.breakpoints.down('sm')]: {
    minWidth: 92,
    padding: 8,
    fontSize: '0.9rem',
    lineHeight: 1.2,
  },
}));

const ApartmentModal = lazy(() => import('./Apartment/ApartmentModal'));

export default function Pricelist() {
  const [selectedApartmentId, setSelectedApartmentId] = useState(null);

  const { t } = useTranslation();
  const { prices, loading, error } = usePricePeriods();

  const priceRows = React.useMemo(() => {
    if (!prices || prices.length === 0) {
      return null;
    }

    const datesMap = new Map();
    const byApartment = new Map();

    prices.forEach((price) => {
      if (!datesMap.has(price.dates)) {
        datesMap.set(price.dates, price);
      }
      if (!byApartment.has(price.apartmentId)) {
        byApartment.set(price.apartmentId, {});
      }
      byApartment.get(price.apartmentId)[price.dates] = price;
    });

    return {
      dates: Array.from(datesMap.keys()),
      byApartment,
    };
  }, [prices]);
  const datesList = React.useMemo(() => {
    if (priceRows?.dates) {
      return priceRows.dates;
    }
    return InitialData[0].price.map((price) => price.dates);
  }, [priceRows]);

  const openApartmentModal = (apartmentId) => {
    setSelectedApartmentId(apartmentId);
  };

  const closeApartmentModal = () => {
    setSelectedApartmentId(null);
  };
  return (
    <Container
      component="section"
      name="prices"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        px: { xs: 1.5, sm: 3 },
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          mb: { xs: 4, sm: 5 },
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Typography
          color="primary.dark"
          align="center"
          variant="h4"
          marked="center"
          mb={5}
          sx={{
            textAlign: 'center',
            pt: { xs: 2, sm: 0 },
            mb: { xs: 3, sm: 5 },
            fontSize: { xs: '1.8rem', sm: '2.125rem' },
          }}
        >
          Prices
        </Typography>
        <Typography
          align="center"
          sx={{
            px: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 3 },
            color: 'text.secondary',
            fontSize: { xs: '0.9rem', sm: '1rem' },
          }}
        >
          {t('apModal.priceDisclaimer')}
        </Typography>
        {error && (
          <Typography color="error" sx={{ px: 2, pb: 2 }}>
            Failed to load prices from the database. Showing fallback data.
          </Typography>
        )}
        <Table sx={{ minWidth: { xs: 760, md: 0 } }}>
          <TableHead>
            <StyledTableRow>
              <TableCell
                sx={{
                  bgcolor: 'secondary.dark',
                  color: 'white',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  minWidth: { xs: 150, sm: 190 },
                }}
              >
                Room/Period
              </TableCell>
              {datesList.map((dates) => (
                <TableCell
                  sx={{
                    bgcolor: 'secondary.dark',
                    color: 'white',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    fontSize: { xs: '0.95rem', sm: '1.2rem' },
                  }}
                  key={dates}
                >
                  {dates}
                </TableCell>
              ))}
            </StyledTableRow>
            {/* <StyledTableRow>
              <TableCell sx={{ bgcolor: 'secondary.main', color: 'white' }}>
                Minimum Stay
              </TableCell>
              {InitialData[0].price.map((price) => (
                <TableCell
                  sx={{
                    bgcolor: 'secondary.main',
                    color: 'white',
                    fontSize: '1.2rem',
                  }}
                  key={price.dates}
                >
                  {price.minStay}
                </TableCell>
              ))}
            </StyledTableRow> */}
          </TableHead>
          <TableBody>
            {InitialData.map((room) => (
              <StyledTableRow
                key={room.id}
                onClick={() => openApartmentModal(room.id)}
              >
                <TableCell
                  component="th"
                  scope="row"
                  sx={{
                    m: -10,
                    cursor: 'pointer',
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    backgroundColor: 'secondary.light',
                    minWidth: { xs: 150, sm: 190 },
                  }}
                >
                  {room.longName}
                </TableCell>
                {datesList.map((dates) => {
                  const price =
                    priceRows?.byApartment?.get(room.id)?.[dates] ||
                    room.price.find((p) => p.dates === dates);
                  return (
                    <TableCell key={dates}>
                      {price ? price.rate : '-'}
                    </TableCell>
                  );
                })}
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
        {loading && (
          <Typography sx={{ px: 2, py: 2 }}>
            Loading prices from the database...
          </Typography>
        )}
      </TableContainer>
      {selectedApartmentId && (
        <Suspense fallback={null}>
          <ApartmentModal
            apartmentId={selectedApartmentId}
            onClose={closeApartmentModal}
          />
        </Suspense>
      )}
    </Container>
  );
}
